import { useState, useEffect, useCallback, useRef } from 'react'
import { streamingManager, StreamingState } from '../services/streamingManager'
import { ChatMessage, AgentStatus } from '../utils/types'

export interface UseStreamingChatOptions {
  accountUrl: string
  endpoint: string
  threadId: string | null
  createThread?: () => Promise<string>
  onDone?: () => void
  onError?: (error: Error) => void
  // Title generation config - when provided, auto-generates title for new threads
  titleGeneration?: {
    agentRunEndpoint: string
    userId: string
    onTitleGenerated?: () => void
  }
}

export interface UseStreamingChatReturn {
  messages: ChatMessage[]
  agentStatus: AgentStatus | null
  isLoading: boolean
  error: Error | null
  parentMessageId: string | null
  sendMessage: (content: string) => Promise<void>
  stop: () => void
  setMessages: (messages: ChatMessage[]) => void
  setThreadId: (threadId: string | null) => void
  setParentMessageId: (id: string | null) => void
  newThread: () => void
}

/**
 * Hook for chat that uses the global streaming manager.
 * This allows streaming to continue even when navigating away.
 */
export function useStreamingChat(options: UseStreamingChatOptions): UseStreamingChatReturn {
  const { accountUrl, endpoint, threadId, createThread, onDone, onError, titleGeneration } = options

  // Local state for when not streaming
  const [localMessages, setLocalMessages] = useState<ChatMessage[]>([])
  const [localParentMessageId, setLocalParentMessageId] = useState<string | null>(null)
  const [localThreadId, setLocalThreadId] = useState<string | null>(threadId)

  // Streaming state from manager
  const [streamingState, setStreamingState] = useState<StreamingState | null>(null)

  // Track if we're subscribed to a specific thread's stream
  const subscribedThreadRef = useRef<string | null>(null)

  // Update local thread ID when prop changes
  useEffect(() => {
    setLocalThreadId(threadId)
  }, [threadId])

  // Subscribe to streaming manager
  useEffect(() => {
    const unsubscribe = streamingManager.subscribe((state) => {
      // Only update if this is for our current thread
      if (state.threadId === localThreadId) {
        setStreamingState(state)
        subscribedThreadRef.current = state.threadId
      }
    })

    // Check if there's already streaming happening for our thread
    const currentState = streamingManager.getState()
    if (currentState && currentState.threadId === localThreadId) {
      setStreamingState(currentState)
      subscribedThreadRef.current = currentState.threadId
    }

    // Check for completed/cached state when mounting
    if (localThreadId) {
      const completedState = streamingManager.getCompletedState(localThreadId)
      if (completedState && !streamingManager.isStreamingForThread(localThreadId)) {
        // Restore from cache if we have it and not currently streaming
        setLocalMessages(completedState.messages)
        setLocalParentMessageId(completedState.parentMessageId)
        // Clear the cache since we've restored it
        streamingManager.clearCompletedState(localThreadId)
      }
    }

    return () => {
      unsubscribe()
      subscribedThreadRef.current = null
    }
  }, [localThreadId])

  // Sync streaming state to local state when stream completes
  useEffect(() => {
    if (streamingState && !streamingState.isLoading && streamingState.threadId === localThreadId) {
      // Stream completed, sync to local state
      setLocalMessages(streamingState.messages)
      setLocalParentMessageId(streamingState.parentMessageId)
      setStreamingState(null)
    }
  }, [streamingState, localThreadId])

  // Track local loading state for the period between sending and streaming start
  const [isLocalLoading, setIsLocalLoading] = useState(false)

  // Determine which state to use
  const isStreaming = streamingState?.threadId === localThreadId && streamingState?.isLoading
  const messages = isStreaming && streamingState ? streamingState.messages : localMessages
  const agentStatus = isStreaming && streamingState ? streamingState.agentStatus : null
  const isLoading = isStreaming || isLocalLoading
  const error = streamingState?.threadId === localThreadId ? streamingState?.error || null : null
  const parentMessageId = isStreaming && streamingState ? streamingState.parentMessageId : localParentMessageId

  const sendMessage = useCallback(
    async (content: string) => {
      if (!content.trim()) return

      // Show user message and loading indicator immediately before any async work
      const shouldGenerateTitle = localMessages.length === 0
      const currentMessages = localMessages

      setLocalMessages((prev) => [
        ...prev,
        {
          id: `pending-user-${Date.now()}`,
          role: 'user' as const,
          content: content.trim(),
          timestamp: new Date(),
          status: 'complete' as const
        },
        {
          id: `pending-assistant-${Date.now()}`,
          role: 'assistant' as const,
          content: '',
          timestamp: new Date(),
          status: 'streaming' as const
        }
      ])
      setIsLocalLoading(true)

      let activeThreadId = localThreadId

      // Create thread if needed
      if (!activeThreadId && createThread) {
        try {
          activeThreadId = await createThread()
          setLocalThreadId(activeThreadId)
        } catch (err) {
          // Revert optimistic messages on thread creation failure
          setLocalMessages(currentMessages)
          setIsLocalLoading(false)
          console.error('[useStreamingChat] Failed to create thread:', err)
          return
        }
      }

      if (!activeThreadId) {
        setLocalMessages(currentMessages)
        setIsLocalLoading(false)
        console.error('[useStreamingChat] No thread ID available')
        return
      }

      setIsLocalLoading(false)

      // Start streaming via the manager - pass original messages (without optimistic ones)
      // since startStreaming creates its own user + assistant messages
      await streamingManager.startStreaming({
        accountUrl,
        endpoint,
        threadId: activeThreadId,
        content,
        parentMessageId: localParentMessageId,
        existingMessages: currentMessages,
        onDone,
        onError,
        // Pass title generation config if this is the first message in the thread
        titleGeneration:
          shouldGenerateTitle && titleGeneration
            ? {
                enabled: true,
                agentRunEndpoint: titleGeneration.agentRunEndpoint,
                userId: titleGeneration.userId,
                onTitleGenerated: titleGeneration.onTitleGenerated
              }
            : undefined
      })
    },
    [
      accountUrl,
      endpoint,
      localThreadId,
      localParentMessageId,
      localMessages,
      createThread,
      onDone,
      onError,
      titleGeneration
    ]
  )

  const stop = useCallback(() => {
    streamingManager.stop()
  }, [])

  const setMessages = useCallback((msgs: ChatMessage[]) => {
    setLocalMessages(msgs)
  }, [])

  const setThreadId = useCallback((id: string | null) => {
    setLocalThreadId(id)
  }, [])

  const setParentMessageId = useCallback((id: string | null) => {
    setLocalParentMessageId(id)
  }, [])

  const newThread = useCallback(() => {
    setLocalMessages([])
    setLocalThreadId(null)
    setLocalParentMessageId(null)
    setStreamingState(null)
    streamingManager.clear()
  }, [])

  return {
    messages,
    agentStatus,
    isLoading,
    error,
    parentMessageId,
    sendMessage,
    stop,
    setMessages,
    setThreadId,
    setParentMessageId,
    newThread
  }
}
